import React, { useState, useMemo } from "react";
import { useAuth } from "../../auth/AuthHandler";


const EMPTY_LIST = Object.freeze([]);
// These are the approved-like statuses used by your existing calendar.
// END allows historical approved leave to appear, when the API returns it.
const ON_LEAVE_STATUSES = new Set(["APPROVED", "END"]);

const normalizeText = (value) => String(value ?? "").trim();
const normalizeEmail = (value) => normalizeText(value).toLowerCase();
const normalizeUpper = (value) => normalizeText(value).toUpperCase();

/**
 * Use calendar date keys, not UTC conversions of local midnight.
 * API strings must begin with YYYY-MM-DD (prefer date-only JSON values).
 * A timestamp's written date is retained; its timezone is not converted.
 */
function toLeaveDateKey(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${String(value.getFullYear()).padStart(4, "0")}-${String(
      value.getMonth() + 1
    ).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/.exec(value.trim());
  if (!match) return null;
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const probe = new Date(0);
  probe.setUTCHours(0, 0, 0, 0);
  probe.setUTCFullYear(year, month - 1, day);
  if (
    year < 1 ||
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) return null;
  return `${yearText}-${monthText}-${dayText}`;
}

/** Normalize the request-level response from /users/onleave-list. */
function normalizeOnLeaveRequests(value) {
  const byId = new Map();
  const list = Array.isArray(value) ? value : EMPTY_LIST;
  list.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") return;
    const status = normalizeUpper(entry.status);
    if (!ON_LEAVE_STATUSES.has(status)) return;

    const startKey = toLeaveDateKey(entry.startDate);
    const hasEndDate = entry.endDate != null && entry.endDate !== "";
    const endKey = hasEndDate ? toLeaveDateKey(entry.endDate) : startKey;
    if (!startKey || !endKey || endKey < startKey) return;

    // Deduplicate repeated requests, not employees. Separate AM/PM requests
    // for the same employee must remain separate entries.
    const rosterKey = entry.id != null
      ? `request-${entry.id}`
      : JSON.stringify([
          normalizeEmail(entry.email) || normalizeText(entry.fullname) || index,
          startKey, endKey, normalizeUpper(entry.category),
          normalizeUpper(entry.timePeriod ?? entry.timeperiod)
        ]);
    byId.set(rosterKey, { ...entry, status, startKey, endKey, rosterKey });
  });
  return Array.from(byId.values());
}

/**
 * Only index dates actually displayed by the two calendar grids.
 * This avoids expanding long requests into an unbounded list of days.
 * Range coverage is inclusive; it does not infer an employee's work schedule.
 */
function buildOnLeaveByDate(requests, cells) {
  const map = new Map();
  const visibleKeys = new Set(cells.map(({ year, month, day }) =>
    `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
  ));
  for (const key of visibleKeys) {
    const entries = requests.filter(
      (entry) => entry.startKey <= key && key <= entry.endKey
    );
    if (entries.length) map.set(key, entries);
  }
  return map;
}

function getOnLeavePeriod(entry) {
  const period = normalizeUpper(entry.timePeriod ?? entry.timeperiod);
  if (period === "AM" || period === "PM") return `Half day (${period})`;
  if (normalizeUpper(entry.category).includes("HALF DAY")) {
    return period ? `Half day (${period})` : "Half day";
  }
  return "Full day";
}

function getOnLeaveName(entry, currentUserEmail) {
  const name = normalizeText(entry.fullname) || normalizeText(entry.email) || "User";
  const mine = normalizeEmail(currentUserEmail) &&
    normalizeEmail(entry.email) === normalizeEmail(currentUserEmail);
  return mine ? `${name} [YOU]` : name;
}

const getDaysInMonth = (year, month) =>
  new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) =>
  new Date(year, month, 1).getDay();
const ymd = (y, m0, d) =>
  `${y}-${String(m0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const getCalendarGrid = (year, month) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDow = getFirstDayOfMonth(year, month); // 0..6 (Sun..Sat)

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;

  const prevDaysInMonth = getDaysInMonth(prevYear, prevMonth);
  const cells = [];

  // tail of previous month
  for (let i = firstDow - 1; i >= 0; i--) {
    const d = prevDaysInMonth - i;
    cells.push({
      day: d,
      month: prevMonth,
      year: prevYear,
      isOutOfRange: true,
    });
  }
  // current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, isOutOfRange: false });
  }
  // head of next month to fill 6x7 grid = 42
  while (cells.length < 42) {
    const d = cells.length - (firstDow + daysInMonth) + 1;
    cells.push({ day: d, month: nextMonth, year: nextYear, isOutOfRange: true });
  }
  return cells;
};


const CalendarPicker = ({
  info = null,
  setInfo,
  isPublicHoliday,
  getHolidayForDate,
  leaveList = EMPTY_LIST,
  leaveAvailable = [],
  onLeaveList = EMPTY_LIST
}) => {

  const monthsList = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const [currentDate, setCurrentDate] = useState(new Date());

  const { userRoles = [], userEmail } = useAuth();

  const [hoveredKey, setHoveredKey] = useState(null);

  const isBoOrFoUser = ["BOUSER", "FOUSER"].some((r) => userRoles.includes(r));

  // ---------------- Map<'YYYY-MM-DD', Entry[]> ----------------
  const tp = (e) =>
    ((e.timeperiod ?? e.timePeriod) ?? "").toString().toUpperCase();
  const cat = (e) => (e.category ?? "").toString().toUpperCase();
  const st = (e) => (e.status ?? "").toString().toUpperCase();
  const normalizedLeaveList = Array.isArray(leaveList) ? leaveList : EMPTY_LIST;
  const onLeaveRequests = useMemo(
    () => normalizeOnLeaveRequests(onLeaveList),
    [onLeaveList]
  );

  const leaveByDate = useMemo(() => {
    const map = new Map();

    for (const e of normalizedLeaveList) {
      const key = toLeaveDateKey(e?.leavedate ?? e?.leaveDate);
      if (!key) continue;

      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }

    return map;
  }, [normalizedLeaveList]);

  // All blocked dates (any leave present), sorted 'YYYY-MM-DD'
  const leaveDateSorted = useMemo(
    () => Array.from(leaveByDate.keys()).sort(),
    [leaveByDate]
  );

  // --------- Helpers ---------
  // Binary search: first index >= target
  const lowerBound = (arr, target) => {
    let lo = 0,
      hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid] < target) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  // First blocked date >= startYMD (or null)
  const firstBlockedOnOrAfter = (startYMD) => {
    const i = lowerBound(leaveDateSorted, startYMD);
    return i < leaveDateSorted.length ? leaveDateSorted[i] : null;
  };

  // Per-day analysis (approved-like = APPROVED/END; pending-like = PENDING)
  function analyzeDay(entries = []) {
    const f = {
      fullApproved: false,
      fullPending: false,
      halfAmApproved: false,
      halfAmPending: false,
      halfPmApproved: false,
      halfPmPending: false,
      hasAny: entries.length > 0,
    };
    for (const e of entries) {
      const isHalf = cat(e).includes("HALF DAY");
      const P = tp(e); // 'AM'|'PM'|''|'FULL'
      const S = st(e); // 'APPROVED'|'END'|'PENDING'|...

      const approvedLike = S === "APPROVED" || S === "END";
      const pendingLike = S === "PENDING";

      if (!isHalf || P === "" || P === "FULL") {
        if (approvedLike) f.fullApproved = true;
        if (pendingLike) f.fullPending = true;
      } else if (P === "AM") {
        if (approvedLike) f.halfAmApproved = true;
        if (pendingLike) f.halfAmPending = true;
      } else if (P === "PM") {
        if (approvedLike) f.halfPmApproved = true;
        if (pendingLike) f.halfPmPending = true;
      }
    }
    return f;
  }

  // UI state for cell coloring
  function dayUiState(entries) {
    const f = analyzeDay(entries);
    const bothHalvesApproved = f.halfAmApproved && f.halfPmApproved;
    const bothHalvesPending = f.halfAmPending && f.halfPmPending;

    if (f.fullApproved || bothHalvesApproved) return "approved-leave";
    if (f.fullPending || bothHalvesPending) return "pending-leave";
    if (f.halfAmApproved || f.halfAmPending) return "half-day-am";
    if (f.halfPmApproved || f.halfPmPending) return "half-day-pm";
    return null;
  }

  // Whether a day can be selected, using entries we already fetched
  function canSelectDay(year, month, day, entries) {
    if (!info?.category) return false;

    const f = analyzeDay(entries || []);
    const isHalfCat = info.category.includes("HALF DAY");
    const desired = (info.timePeriod ?? "").toUpperCase();

    const targetYMD = ymd(year, month, day);
    const startYMD = info?.startDate
      ? ymd(
          info.startDate.getFullYear(),
          info.startDate.getMonth(),
          info.startDate.getDate()
        )
      : null;

    // ---------- FULL-DAY ----------
    if (!isHalfCat) {
      // exact date blocked if any leave exists that day
      if (f.hasAny) return false;

      // when choosing END (start chosen, end not chosen yet, clicking a date >= start),
      // do not allow crossing the first blocked date on/after start.
      if (startYMD && !info.endDate && targetYMD >= startYMD) {
        const firstBlocked = firstBlockedOnOrAfter(startYMD); // e.g., '2025-08-06'
        if (firstBlocked && targetYMD >= firstBlocked) return false; // so only up to '2025-08-05'
      }
      return true;
    }

    // ---------- HALF-DAY ----------
    const bothHalvesTaken =
      (f.halfAmApproved || f.halfAmPending) &&
      (f.halfPmApproved || f.halfPmPending);

    // full-day or both halves block selection
    if (f.fullApproved || f.fullPending || bothHalvesTaken) return false;

    // respect chosen half
    if (desired === "AM") return !(f.halfAmApproved || f.halfAmPending);
    if (desired === "PM") return !(f.halfPmApproved || f.halfPmPending);

    // if no half chosen yet, selectable if at least one half is free
    return (
      !(f.halfAmApproved || f.halfAmPending) ||
      !(f.halfPmApproved || f.halfPmPending)
    );
  }

  // Click handler with auto-pick half if needed and allowed
  const handleDateClick = (year, month, day) => {
    if (!info?.category) return;

    const key = ymd(year, month, day);
    const entries = leaveByDate.get(key) || [];
    const f = analyzeDay(entries);

    // Respect selectability
    if (!canSelectDay(year, month, day, entries)) return;

    // For half-day category, auto-pick a free half if not chosen (prefer AM)
    if (info.category.includes("HALF DAY")) {
      let desired = (info.timePeriod ?? "").toUpperCase();

      const amTaken = f.halfAmApproved || f.halfAmPending;
      const pmTaken = f.halfPmApproved || f.halfPmPending;

      if (!desired) {
        if (!amTaken) desired = "AM";
        else if (!pmTaken) desired = "PM";
      } else {
        // If currently chosen half is unavailable, switch to the other if free
        if (desired === "AM" && amTaken && !pmTaken) desired = "PM";
        if (desired === "PM" && pmTaken && !amTaken) desired = "AM";
      }

      if (desired && desired !== info.timePeriod) setInfo("timePeriod", desired);
    }

    // Range selection logic
    const clicked = new Date(year, month, day);
    if (!info.startDate) {
      setInfo("startDate", clicked);
      if (info.category?.includes("HALF DAY")) {
        setInfo("endDate", clicked);
      } else {
        setInfo("endDate", null);
      }
    } else if (!info.endDate && clicked >= info.startDate) {
      setInfo("endDate", clicked);
    } else {
      setInfo("startDate", clicked);
      if (info.category?.includes("HALF DAY")) {
        setInfo("endDate", clicked);
      } else {
        setInfo("endDate", null);
      }
    }
  };

  const isDateInRange = (year, month, day) => {
    if (!info?.startDate || !info?.endDate) return false;
    const d = new Date(year, month, day);
    return d >= info.startDate && d <= info.endDate;
  };

  // --------- Current & next month ---------
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const nextMonth = currentMonth + 1 > 11 ? 0 : currentMonth + 1;
  const nextMonthYear = nextMonth === 0 ? currentYear + 1 : currentYear;

  const currentMonthDays = useMemo(
    () => getCalendarGrid(currentYear, currentMonth),
    [currentYear, currentMonth]
  );
  const nextMonthDays = useMemo(
    () => getCalendarGrid(nextMonthYear, nextMonth),
    [nextMonthYear, nextMonth]
  );

  // DISPLAY ONLY: never merge this map into leaveByDate. A colleague's
  // leave must not block the logged-in user's request dates.
  const onLeaveByDate = useMemo(
    () => buildOnLeaveByDate(onLeaveRequests, [...currentMonthDays, ...nextMonthDays]),
    [onLeaveRequests, currentMonthDays, nextMonthDays]
  );

  const handlePreviousMonth = () => {
    setHoveredKey(null);
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  const handleNextMonth = () => {
    setHoveredKey(null);
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  const handleMonthChange = (event) => {
    const month = Number(event.target.value);
    if (!Number.isInteger(month) || month < 0 || month > 11) return;
    setHoveredKey(null);
    setCurrentDate((prev) => new Date(prev.getFullYear(), month, 1));
  };
  const handleYearChange = (event) => {
    const year = Number(event.target.value);
    if (!Number.isInteger(year)) return;
    setHoveredKey(null);
    setCurrentDate((prev) => new Date(year, prev.getMonth(), 1));
  };

  // Year options centered around current year (11 years window)
  const yearOptions = useMemo(() => {
    const years = [];
    const start = currentYear - 5;
    const end = currentYear + 5;
    for (let y = start; y <= end; y++) years.push(y);
    return years;
  }, [currentYear]);

  // ------------ All entries on hovered date --------------------
  const hoveredEntries = useMemo(
    () => (hoveredKey ? leaveByDate.get(hoveredKey) || [] : []),
    [hoveredKey, leaveByDate]
  );

  const hoveredOnLeaveEntries = useMemo(
    () => hoveredKey ? onLeaveByDate.get(hoveredKey) || EMPTY_LIST : EMPTY_LIST,
    [hoveredKey, onLeaveByDate]
  );

  // For BOUSER/FOUSER, their own leaves on that date
  const myHoveredEntries = useMemo(
    () =>
      isBoOrFoUser && hoveredEntries.length
        ? hoveredEntries.filter(
            (e) =>
              (e.email || "").toLowerCase() ===
              (userEmail || "").toLowerCase()
          )
        : [],
    [hoveredEntries, isBoOrFoUser, userEmail]
  );

  // Parsed date info for hovered day (for title)
  const hoveredDateInfo = useMemo(() => {
    if (!hoveredKey) return null;
    const [y, m, d] = hoveredKey.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    const jsDate = new Date(y, m - 1, d);
    return {
      jsDate,
      label: jsDate.toLocaleDateString(undefined, {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      year: y,
      month0: m - 1,
      day: d,
    };
  }, [hoveredKey]);

  const handleCellMouseEnter = (year, month, day) => {
    const key = ymd(year, month, day);
    setHoveredKey(key);
  };

  const handleCellMouseLeave = () => {
    setHoveredKey(null);
  };

  // --------- Render table (shared) ---------
  // Render helper, not a nested component. Hover state changes must not
  // replace the table's component type and remount its DOM.
  const renderMonthTable = (days) => (
    <table className="calendar-grid-container">
      <thead>
        <tr>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
            <th key={i} className="weekday">
              {d}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...Array(6)].map((_, rowIndex) => {
          const startIdx = rowIndex * 7;
          const rowDays = days.slice(startIdx, startIdx + 7);

          return (
            <tr key={rowIndex}>
              {rowDays.map((day, colIndex) => {
                const { year, month, day: dd } = day;
                const isWeekend = colIndex === 0 || colIndex === 6;
                const holidayObj = getHolidayForDate(year, month, dd);
                const isHoliday = !!holidayObj;
                const inRange = isDateInRange(year, month, dd);

                const key = ymd(year, month, dd);
                const entries = leaveByDate.get(key) || EMPTY_LIST;
                const rosterEntries = onLeaveByDate.get(key) || EMPTY_LIST;
                const state = dayUiState(entries); // 'approved-leave' | 'pending-leave' | 'half-day-am' | 'half-day-pm' | null

                const isHalfCat = !!info?.category?.includes("HALF DAY");
                const hasAnyLeaveForDate = leaveByDate.has(key);
                const blockedByFullDayRule = !isHalfCat && hasAnyLeaveForDate;

                const selectable =
                  !day.isOutOfRange &&
                  !blockedByFullDayRule &&
                  canSelectDay(year, month, dd, entries);

                const dayClass = `
                  day
                  ${day.isOutOfRange ? "out-range" : ""}
                  ${!selectable ? "isNotSelectable" : ""}
                  ${
                    isWeekend
                      ? "weekend"
                      : isHoliday
                      ? "public-holiday"
                      : state
                      ? `on-leave ${state}`
                      : ""
                  }
                  ${
                    inRange && !isHoliday && !isWeekend ? "in-range" : ""
                  }

                  ${
                    info?.startDate?.getFullYear() === year &&
                    info?.startDate?.getMonth() === month &&
                    info?.startDate?.getDate() === dd
                      ? "start"
                      : ""
                  }
                  ${
                    info?.endDate?.getFullYear() === year &&
                    info?.endDate?.getMonth() === month &&
                    info?.endDate?.getDate() === dd
                      ? "end"
                      : ""
                  }
                `;

                return (
                  <td
                    key={colIndex}
                    className={dayClass}
                    // Disabled date selection must not disable hover details.
                    style={{ pointerEvents: "auto" }}
                    tabIndex={day.isOutOfRange ? -1 : 0}
                    aria-label={key}
                    aria-disabled={!selectable}
                    onFocus={() => handleCellMouseEnter(year, month, dd)}
                    onBlur={handleCellMouseLeave}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (selectable) handleDateClick(year, month, dd);
                      }
                    }}
                    onClick={
                      selectable
                        ? () => handleDateClick(year, month, dd)
                        : undefined
                    }
                    onMouseEnter={() =>
                      handleCellMouseEnter(year, month, dd)
                    }
                    onMouseLeave={handleCellMouseLeave}
                    title={(() => {
                      const lines = [];

                      if (holidayObj) {
                        lines.push(
                          holidayObj.holidayName
                            ? `Public holiday – ${holidayObj.holidayName}`
                            : "Public holiday"
                        );
                      }

                      if (isWeekend) lines.push("Weekend");

                      if (entries.length) {
                        entries.forEach((e) => {
                          const mine =
                            isBoOrFoUser &&
                            (e.email || "").toLowerCase() ===
                              (userEmail || "").toLowerCase();
                          const who = mine
                            ? "[YOU]"
                            : e.fullname || e.email || "User";
                          const catLabel = e.category || "Leave";
                          const tpLabel = e.timePeriod || e.timeperiod || "";
                          const statusLabel = e.status || "";
                          lines.push(
                            `${who} - ${catLabel} ${tpLabel} (${statusLabel})`.trim()
                          );
                        });
                      }

                      lines.push("Staff on leave on this date:");
                      if (rosterEntries.length) {
                        rosterEntries.forEach((entry) => {
                          lines.push(
                            `${getOnLeaveName(entry, userEmail)} - ${entry.category || "Leave"} - ${getOnLeavePeriod(entry)}`
                          );
                        });
                      } else {
                        lines.push("No approved leave listed for this date.");
                      }
                      return lines.join("\n");
                    })()}
                  >
                    {dd}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="calendar-picker">
      <div className="calendar-header">
        <button type="button" className="calendar-nav-btn" onClick={handlePreviousMonth}>
          &lt;
        </button>

        <div className="month-year-inline">
          {/* "FEB 2025" – selectable */}
          <div className="period-select-group">
            <div className="select-wrapper">
              <select
                className="period-select month-select"
                value={currentMonth}
                onChange={handleMonthChange}
              >
                {monthsList.map((label, idx) => (
                  <option key={idx} value={idx}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="select-wrapper">
              <select
                className="period-select year-select"
                value={currentYear}
                onChange={handleYearChange}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* "/" separator */}
          <span className="month-separator">/</span>

          {/* "MAR 2025" – read-only */}
          <span className="next-period-label">
            {monthsList[nextMonth]} {nextMonthYear}
          </span>
        </div>

        <button type="button" className="calendar-nav-btn" onClick={handleNextMonth}>
          &gt;
        </button>
      </div>

      <div className="calendar-grid-wrapper">
        {renderMonthTable(currentMonthDays)}
        {renderMonthTable(nextMonthDays)}
      </div>

      {/* Day activities panel – under the calendars */}
      <div className="day-activity-panel">
        <h6 className="day-activity-title">Day activities</h6>

        {!hoveredDateInfo && (
          <p className="day-activity-empty text-muted mb-0">
            Hover over or focus a date to see who is on leave.
          </p>
        )}

        {hoveredDateInfo && (
          <>
            <div className="day-activity-date">{hoveredDateInfo.label}</div>

            <ul className="day-activity-list list-unstyled mb-0">
              {/* Weekend / Public holiday */}
              {(() => {
                const items = [];
                const dayOfWeek = hoveredDateInfo.jsDate.getDay();

                // use the object helper here
                const holidayObj = getHolidayForDate(
                  hoveredDateInfo.year,
                  hoveredDateInfo.month0,
                  hoveredDateInfo.day
                );

                if (holidayObj) {
                  items.push(
                    <li key="holiday">
                      <span className="badge bg-warning-subtle text-warning-emphasis me-2">
                        Holiday
                      </span>
                      Public holiday
                      {holidayObj.holidayName ? ` - ${holidayObj.holidayName}` : ""}
                    </li>
                  );
                }

                if (dayOfWeek === 0 || dayOfWeek === 6) {
                  items.push(
                    <li key="weekend">
                      <span className="badge bg-danger-subtle text-danger-emphasis me-2">
                        Weekend
                      </span>
                      Weekend
                    </li>
                  );
                }

                return items;
              })()}

              {/* My leave (for BOUSER / FOUSER) */}
              {isBoOrFoUser && myHoveredEntries.length > 0 && (
                <>
                  <li className="mt-2 fw-semibold">
                    Your leave on this day:
                  </li>
                  {myHoveredEntries.map((e, idx) => (
                    <li key={`me-${idx}`} className="ms-2">
                      <span className="badge bg-primary-subtle text-primary-emphasis me-2">
                        {e.status || "PENDING"}
                      </span>
                      {e.category || "Leave"}
                      {(e.timePeriod ?? e.timeperiod) ? ` (${e.timePeriod ?? e.timeperiod})` : ""}
                    </li>
                  ))}
                </>
              )}

              {/* All leaves on this day (if any) */}
              {hoveredEntries.length > 0 && (
                <>
                  <li className="mt-2 fw-semibold">
                    Leave requests on this day:
                  </li>
                  {hoveredEntries.map((e, idx) => (
                    <li key={`all-${idx}`} className="ms-2">
                      <span className="badge bg-secondary-subtle text-secondary-emphasis me-2">
                        {e.status || "PENDING"}
                      </span>
                      {(e.fullname || e.email || "User")} –{" "}
                      {e.category || "Leave"}
                      {(e.timePeriod ?? e.timeperiod) ? ` (${e.timePeriod ?? e.timeperiod})` : ""}
                    </li>
                  ))}
                </>
              )}

              {/* Request ranges from /users/onleave-list, independent of own leave. */}
              <li className="mt-2 fw-semibold">
                Staff on leave on this date:
              </li>
              {hoveredOnLeaveEntries.length > 0 ? (
                hoveredOnLeaveEntries.map((entry) => (
                  <li key={entry.rosterKey} className="ms-2">
                    <span className="badge bg-secondary-subtle text-secondary-emphasis me-2">
                      {entry.status}
                    </span>
                    <strong>{getOnLeaveName(entry, userEmail)}</strong>
                    {" - "}{entry.category || "Leave"}
                    {" - "}{getOnLeavePeriod(entry)}
                  </li>
                ))
              ) : (
                <li className="text-muted small ms-2">
                  No approved leave listed for this date.
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export { toLeaveDateKey, normalizeOnLeaveRequests, buildOnLeaveByDate, getOnLeavePeriod };
export default CalendarPicker;
